import {makeAutoObservable, runInAction} from "mobx";
import {getStore, useStore} from "../index";
import {ObjectId} from 'bson';
import {
    INIT_BOARD_OPTION, INIT_FORM_CONTENT, INIT_FORM_OPTION, INIT_FORM_SUB_TYPE,
    INIT_PAGE_OPTION,
    INIT_SUB_TYPE,
    INIT_TEMPLATE_OPTION
} from "@/app/admin/(protected)/menu/controller";
import axios from "axios";
import {setSortTreeMenu} from "@/utils/menu";


/**
 * 관리자 사이드 네비게이션 메뉴 아이템
 * @typedef {object} SideNavigationItem
 * @property {string} title - 메뉴명
 * @property {boolean} custom - 커스텀 메뉴 여부
 * @property {string} [path] - 이동 경로
 * @property {Array<SideNavigationItem>} [children] - 하위 메뉴 리스트
 */



/**
 * admin 메뉴 조작 관련 상태 관리 Store
 */
export class MenuStore {

    /**
     * 관리자 사이드 네비게이션 데이터
     * @type {Array<SideNavigationItem>}
     */
    sideNavigation = [
        { title: "관리자 메인", custom: false, path: "/admin/dashboard"},
        { title: "메뉴관리", custom: false, path: "/admin/menu" },
        { title: "컨텐츠 관리", custom: true, children: [] },
        {
            title: "운영관리",
            custom: false,
            children: [
                { title: "회원관리", custom: false, path: "/admin/management/user" },
                { title: "배너관리", custom: false, path: "/admin/management/banner" },
                { title: "팝업관리", custom: false, path: "/admin/management/popup" },
                {
                    title: "기본정보 관리",
                    custom: false,
                    children: [
                        { title: "Footer 영역", custom: false, path: "/admin/management/information/footer" },
                    ]
                },
            ]
        },
    ];

    /**
     * 원본 메뉴 트리 리스트
     * @type {Array<Object>}
     */
    menuTreeList = [];

    /**
     * 정렬 및 가공된 메뉴 트리 리스트
     * @type {Array<Object>}
     */
    sortMenuTreeList = [];

    /**
     * 펼쳐진 메뉴 ID 리스트
     * @type {Array<string>}
     */
    openIds = [];

    /**
     * 현재 추가 중인 부모 메뉴 ID
     * @type {string|null}
     */
    addingUnder = null;

    /**
     * 임시 입력 메뉴명
     * @type {string}
     */
    draftName = "";

    /**
     * 현재 선택된 메뉴 ID
     * @type {string|null}
     */
    selectMenuItemId = null;

    /**
     * 현재 선택된 컨텐츠 데이터
     * @type {Object|null}
     */
    currentContent = null;

    /**
     * 현재 페이지명
     * @type {string}
     */
    currentPageName = "";

    /**
     * MenuStore 생성자
     * @param {Object} store - Root Store
     * @param {Object} initialNavigation - 초기 메뉴 데이터
     */
    constructor(store, initialNavigation) {
        this.store = store;
        makeAutoObservable(this, {}, {autoBind: true});
        if (initialNavigation) {
            if(initialNavigation && initialNavigation.result){
                this.setCurrentMenu(initialNavigation.data.menus);
            }
        }
    }

    /**
     * 현재 메뉴 데이터 설정
     * @param {Array<Object>} menuData - 메뉴 데이터
     */
    setCurrentMenu(menuData) {
        this.menuTreeList = menuData;
        this.sideNavigation = this.sideNavigation.map(item => item.custom ? { ...item, children: this.menuTreeList } : item);
    }

    /**
     * 메뉴 트리 정렬 데이터 생성
     */
    getSortTreeMenu() {
        this.sortMenuTreeList = setSortTreeMenu(this.menuTreeList, "root", [])
    }

    /**
     * 정렬 메뉴 리스트 변경
     * @param {Array<Object>} list - 변경할 리스트
     */
    setChangeListData(list) {
        this.sortMenuTreeList = Array.isArray(list) ? list : [];
    }

    /**
     * 메뉴 펼침/닫힘 토글
     * @param {string} id - 메뉴 ID
     */
    toggleOpen = (id) => {
        this.openIds = this.openIds.includes(id) ? this.openIds.filter(idx => idx !== id) : [...this.openIds, id];
    };

    /**
     * 메뉴 강제 펼침 처리
     * @param {string} id - 메뉴 ID
     */
    ensureOpen = (id) => {
        if (!this.openIds.includes(id)) {
            this.openIds = [...this.openIds, id];
        }
    };

    /**
     * 하위 폴더 추가 상태 설정
     * @param {string} parentId - 부모 메뉴 ID
     */
    setNewFolderAdd = (parentId) => {
        this.addingUnder = parentId;
        this.draftName = "";
        this.ensureOpen(parentId);
    };

    /**
     * 임시 메뉴명 설정
     * @param {string} name - 메뉴명
     */
    setDraftName(name) {
        this.draftName = name ?? "";
    }

    /**
     * 특정 부모 메뉴 아래 첫 번째 위치에 노드 추가
     * @param {Array<Object>} list - 메뉴 리스트
     * @param {string} parentId - 부모 메뉴 ID
     * @param {Object} node - 추가할 노드
     * @returns {Array<Object>}
     */
    setInsertFirst(list, parentId, node) {
        const out = [...list];
        const parentIdx = out.findIndex(node => node.id === parentId);
        if (parentIdx === -1) {
            out.push(node);
            return out;
        }
        let lastChildIdx = -1;
        out.forEach((node, idx) => {
            if (node.parent === parentId) {
                lastChildIdx = idx;
            }
        });
        const insertIdx = lastChildIdx === -1 ? parentIdx + 1 : lastChildIdx + 1;
        out.splice(insertIdx, 0, node);
        return out;
    }

    /**
     * 폴더 추가
     * @returns {boolean|undefined}
     */
    setFolderAdd = () => {
        const name = this.draftName;
        const parentId = this.addingUnder;
        if (!parentId) return false;
        const newNode = {
            id: new ObjectId().toHexString(), // 벡엔드 데이터와 맞추기 위한 몽고용 ID 세팅
            parent: parentId,
            text: name,
            type: "FOLDER"
        };
        this.sortMenuTreeList = this.setInsertFirst(this.sortMenuTreeList, parentId, newNode);
        this.ensureOpen(parentId);
        this.setSelectMenuSetting(newNode);
        this.addingUnder = null;
        this.draftName = "";
        return true;
    };

    /**
     * 폴더 추가 취소
     */
    setFolderCancelAdd() {
        this.addingUnder = null;
        this.draftName = "";
    }

    /**
     * 현재 선택된 노드 반환
     * @returns {Object|null}
     */
    getSelectedNode() {
        if (!this.selectMenuItemId) return null;
        return this.sortMenuTreeList.find(node => node.id === this.selectMenuItemId) || null;
    }

    /**
     * 선택된 메뉴 삭제
     */
    setDeleteMenu() {
        this.sortMenuTreeList = this.sortMenuTreeList.filter(node => node.id !== this.selectMenuItemId);
    }

    /**
     * 메뉴 선택 상태 변경
     * @param {Object} node - 메뉴 노드
     */
    setSelectMenuSetting(node) {
        if(this.selectMenuItemId && this.selectMenuItemId === node.id){
            this.selectMenuItemId = null;
        }else {
            this.selectMenuItemId = node.id ?? null;
        }
    }

    /**
     * Form Row Select 옵션 확정 처리
     * @param {string} rowId - Row ID
     */
    setFormCommitRowOption(rowId) {
        const selectMenuItem = this.getSelectedNode();
        const row = selectMenuItem.form.find(item => item._id === rowId);
        const opts = (row.rawOptions || "").split(",").map(string => string.trim()).filter(Boolean).map(value => ({ label: value, value: value }));
        row.options = opts;
        if (!opts.find(options => options.value === row.selected)) row.selected = null;
    }

    /**
     * Form 데이터 변경
     * @param {string} formId - Form ID
     * @param {*} value - 변경 값
     * @param {string|undefined} type - 변경 타입
     */
    setSelectMenuFormChange(formId, value, type = undefined) {
        const selectMenuItem = this.getSelectedNode();
        const row = selectMenuItem.form.find(item => item._id === formId);
        if(type === "name"){
            row.name = value;
        } else if(type === "select"){
            row.rawOptions = value;
        } else if(type === "isSelect"){
            row.selected = value;
        } else{
            row.value = value;
        }
    }

    /**
     * Form Row 삭제
     * @param {string} rowId - Row ID
     */
    setFormRemoveRow(rowId) {
        const selectMenuItem = this.getSelectedNode();
        selectMenuItem.form = selectMenuItem.form.filter(item => item._id !== rowId);
    }

    /**
     * Form Row 추가
     * @param {string} formId - 기준 Form ID
     * @param {string} type - Row 타입
     */
    setFormAddRow(formId, type) {
        const selectMenuItem = this.getSelectedNode();
        const rowLength = selectMenuItem.form.length
        const index = selectMenuItem.form.findIndex(item => item._id === formId);
        const newRow = this.setFormCreateRowTemp(rowLength, type);
        selectMenuItem.form = [...selectMenuItem.form.slice(0, index + 1), newRow, ...selectMenuItem.form.slice(index + 1, rowLength),];
    }

    /**
     * Form Row 기본 데이터 생성
     * @param {string|number} newId - Row ID
     * @param {string} type - Row 타입
     * @returns {Object}
     */
    setFormCreateRowTemp(newId, type) {
        const base = { _id: `${newId}`, type, name: "", used: false, required: false };
        switch (type) {
            case "select": return { ...base, rawOptions: "", options: [], selected: null };
            case "text": return { ...base };
            case "tel": return { ...base };
            case "email": return { ...base };
            case "content":case "agree": return { ...base, value: "" };
            default: return { ...base, type: "text" };
        }
    }

    /**
     * Form 타입 내에 Row 순서 변경
     * @param {number} from - 기존 인덱스
     * @param {number} to - 이동 인덱스
     */
    reorderSelectMenuForm(from, to) {
        const node = this.getSelectedNode();
        if (!node || !Array.isArray(node.form)) return;
        const arr = node.form.slice();
        const [moved] = arr.splice(from, 1);
        arr.splice(to, 0, moved);
        node.form = arr;
    }



    /**
     * 메뉴 옵션 변경
     * @param {string} type - 옵션 타입
     * @param {*} value - 변경 값
     * @param {boolean} option - 옵션 여부
     * @param {string|undefined} subType - 하위 타입
     */
    setSelectMenuOptionsChange(type, value, option = false, subType = undefined) {
        const found = this.getSelectedNode();
        if (!found) return;
        if (option) {
            if (!found.options) {
                found.options = {};
            }
            if (subType) {
                if (!found.options[type] || typeof found.options[type] !== "object") {
                    found.options[type] = {};
                }
                found.options[type][subType] = value;
            } else {
                found.options[type] = value;
            }
        } else {
            found[type] = value;
        }
    }

    /**
     * 메뉴 ID 초기화
     */
    setInitNodeId(){
        const found = this.getSelectedNode();
        if (!found) return;
        if(found.id){
            found.id = new ObjectId().toHexString();
        }
        if(!this.getSelectedNode()){
            this.setSelectMenuSetting(found);
        }
    }

    /**
     * 메뉴 상태 초기화
     */
    setInitMenu() {
        this.sortMenuTreeList = [];
        this.openIds = [];
        this.addingUnder = null;
        this.draftName = "";
        this.selectMenuItemId = null;
        this.currentContent = null;
    }

    /**
     * 메뉴 타입별 초기값 설정
     * @param {Object} node - 메뉴 노드
     * @param {string} type - 메뉴 타입
     */
    setInitValueOption(node, type) {
        if (!node) return;
        if(!this.getSelectedNode()){
            this.setSelectMenuSetting(node);
        }
        if(type === "PAGE"){
            node.options = INIT_PAGE_OPTION;
            if(node.subType){
                delete node.subType;
                delete node.form;
            }
        } else if(type === "FORM"){
            node.form = INIT_FORM_CONTENT;
            node.options = INIT_FORM_OPTION;
            node.subType = INIT_FORM_SUB_TYPE;
        }  else if(type === "BOARD"){
            node.options = INIT_BOARD_OPTION;
            node.subType = INIT_SUB_TYPE;
        } else if(type === "TEMPLATE" || type === "E-NEWS"){
            node.options = INIT_TEMPLATE_OPTION;
            if(node.subType){
                delete node.subType;
                delete node.form;
            }
        } else if(type === "FOLDER"){
            delete node.subType;
            delete node.form;
            delete node.options;
        }
    }

    /**
     * 메뉴 저장전 트리 정규화
     * @returns {Array<Object>}
     */
    setNormalizeMenuTree() {
        const flat = this.sortMenuTreeList;
        const byId = {};
        flat.forEach(item => {
            const type = item.type;
            const node = {
                menuId: item.menuId || item.id,
                title: item.title || item.text,
                type
            };
            if(item.mainDisplay){
                node["mainDisplay"] = item.mainDisplay;
            }
            if(type === "PAGE"){
                node["component"] = item.component;
            }
            if (type === "FOLDER") {
                if (item.required !== undefined){
                    node.required = item.required;
                }
            } else {
                if (item.subType !== undefined){
                    node.subType = item.subType;
                }
                if (item.options !== undefined){
                    node.options = item.options;
                }
                if (item.form !== undefined){
                    node.form = item.form;
                }
                if (item.required !== undefined){
                    node.required = item.required;
                }
            }
            byId[item.id] = { node, parent: item.parent };
        });
        const roots = [];
        flat.forEach(item => {
            const entry = byId[item.id];
            const parentId = entry.parent;
            if (!parentId || parentId === "root") {
                roots.push(entry.node);
            } else {
                const p = byId[parentId];
                if (p && p.node) {
                    if (!p.node.children) p.node.children = [];
                    p.node.children.push(entry.node);
                } else {
                    roots.push(entry.node);
                }
            }
        });
        return roots;
    }

    /**
     * 메뉴 업데이트 API 요청
     * @param {Array<Object>} list - 저장할 메뉴 리스트
     * @returns {Promise<void>}
     */
    async setMenuUpdate(list) {
        const response = await axios.post("/api/menu", {
            data: list
        });
        if(response){
            const response = await axios.get("/api/menu");
            if(!response.data.result) return;
            this.setCurrentMenu(response.data.data.menus)
        }
    }
}

/**
 * MenuStore Hook
 * @returns {MenuStore}
 */
export const useMenuStore = () => {
    return useStore().menuStore;
}

/**
 * MenuStore Getter
 * @returns {MenuStore}
 */
export const getMenuStore = () => {
    return getStore().menuStore;
}
